/**
 * layoutStore - where a surface's arrangement is kept, and whose it is.
 *
 * THE BUG THIS EXISTS TO FIX: layouts were written to bare keys —
 * `matter:layout:v1:<matterId>`, `review:layout:v2` - with no user in them.
 * On a shared machine, or after switching accounts in the same browser, the
 * next person inherited the previous one's arrangement, and there was no way
 * to tell whose a stored layout was. Every key here carries the user:
 *
 *     pons:layout:v1:<userId>:<kind>:<name>[:<entityId>]
 *
 * `kind` and `name` come from the surface's config - `detail` / `matter`,
 * `detail` / `review` - and `entityId` is present only for surfaces scoped to
 * one record: a matter remembers its own arrangement, a review session shares
 * one across every session.
 *
 * Two things beyond get/set:
 *
 *   PRUNE. A per-entity surface grows a key per record touched, forever. The
 *   store keeps a recency index and keeps the newest `MAX_PER_GROUP` of each
 *   `<kind>:<name>` group, so a user who opens a thousand matters does not
 *   accumulate a thousand dead layout keys.
 *
 *   MIGRATE. The unscoped keys already in people's browsers hold arrangements
 *   they built by hand. `migrateLegacyLayouts` copies them into the scoped
 *   namespace once, so nobody's panes move on the day this ships.
 *
 * Reads are repair-parsed through `validateLayout`, so storage stays what it
 * has always been here: untrusted input that degrades to the defaults rather
 * than rendering a broken surface.
 *
 * THE SERVER IS NOW THE SOURCE OF TRUTH, and `localStorage` is a cache in
 * front of it (`layoutTransport`). The split of responsibilities:
 *
 *   • READS stay SYNCHRONOUS and local, because every surface reads its
 *     layout in a `useState` initialiser and an awaited read would mean every
 *     page painted on the defaults and then jumped. The cache answers first;
 *     `hydrateScoped` then asks the server and reconciles.
 *   • WRITES go to `localStorage` immediately (so a reload is instant and an
 *     offline session still remembers) and to the server on a debounce.
 *   • A scope only talks to the server once something has HYDRATED it. That
 *     makes remote sync opt-in per surface: a caller that never hydrates —
 *     and every existing unit test - behaves exactly as it did before.
 */

import {
  PanelLayout,
  PanelSurface,
  validateLayout,
} from '../../components/shared/surface/panelLayout';
import {
  RemoteLayoutScope,
  deleteRemoteLayout,
  readRemoteLayout,
  writeRemoteLayout,
} from './layoutTransport';

/** The whole namespace, so a prune or a wipe can find its own keys. */
export const LAYOUT_KEY_PREFIX = 'pons:layout';

/** Newest layouts kept per `<userId>:<kind>:<name>` group. */
export const MAX_PER_GROUP = 50;

/**
 * Local key → remote scope.
 *
 * The remote path needs `kind`/`name`/`entityId` UNENCODED, and the local key
 * has them run through `segment()`, which is deliberately lossy (it swaps `%`
 * for `_` so a segment can never contain the `:` separator). Rather than try
 * to invert that, `scopeKey` records what it was given. Every read and write
 * builds its key through `scopeKey` first, so the entry is always there by the
 * time anything needs it; a key we have never seen simply stays local-only.
 */
const KEY_SCOPES = new Map<string, RemoteLayoutScope>();

export interface LayoutScope {
  /** Family of surface - `detail` for a canvas-and-panes page. */
  kind: string;
  /** The surface within that family - `matter`, `review`. */
  name: string;
  /** Bumped when the stored model changes shape. */
  version?: string;
  /** Whose layout this is. Falsy degrades to `anon` rather than an unkeyed write. */
  userId?: string | null;
  /** Present only for surfaces that remember one arrangement per record. */
  entityId?: string | null;
}

/** The user segment for someone we cannot identify yet (pre-profile boot). */
export const ANONYMOUS_USER = 'anon';

/**
 * `:` separates the segments, so a segment may not contain one.
 *
 * Ids are uuids in practice, but a name typed by a human could carry
 * anything; encoding rather than stripping keeps distinct values distinct.
 */
const segment = (value: string): string =>
  encodeURIComponent(String(value)).replace(/%/g, '_');

export function scopeKey({
  kind,
  name,
  version = 'v1',
  userId,
  entityId,
}: LayoutScope): string {
  const parts = [
    LAYOUT_KEY_PREFIX,
    segment(version),
    segment(userId || ANONYMOUS_USER),
    segment(kind),
    segment(name),
  ];
  if (entityId) parts.push(segment(entityId));
  const key = parts.join(':');
  /*
    Remember the UNENCODED scope - the server path needs it, and the key
    cannot be inverted back into one. See `KEY_SCOPES`.

    THE VERSION GOES IN THE REMOTE NAME, because the server keys on
    kind:name and nothing else. `RemoteLayoutScope` has no version field, so
    without this every version of a surface reads and writes the SAME record:
    a bump invalidates the local cache, hydration hands the old arrangement
    straight back on the next mount, and the new defaults never survive first
    paint. Review demonstrated it - a v6 default of "open, docked right" kept
    coming back as the v4 "docked left" the server still held under
    `detail:review`.

    `v1` stays BARE so every surface currently on v1 keeps the record it has;
    only a surface that has deliberately bumped gets a new one, which is
    exactly what bumping is for.
  */
  const remoteName = version === 'v1' ? name : `${name}_${version}`;
  KEY_SCOPES.set(key, { kind, name: remoteName, entityId: entityId ?? null });
  return key;
}

/* ------------------------------------------------------------------ */
/*  The server, and which keys talk to it                              */
/* ------------------------------------------------------------------ */

/** Keys that have been hydrated, and therefore sync. See the module doc. */
const REMOTE_KEYS = new Set<string>();

/** How long a burst of layout edits is allowed to coalesce into one PUT. */
export const LAYOUT_SYNC_DEBOUNCE_MS = 800;

interface PendingWrite {
  timer: ReturnType<typeof setTimeout>;
  value: unknown;
}
const PENDING = new Map<string, PendingWrite>();

const cancelPending = (key: string): void => {
  const pending = PENDING.get(key);
  if (!pending) return;
  clearTimeout(pending.timer);
  PENDING.delete(key);
};

const sendWrite = (key: string, value: unknown): void => {
  const scope = KEY_SCOPES.get(key);
  if (!scope) return;
  // Fire and forget: `writeRemoteLayout` resolves false rather than rejecting,
  // and a failed sync must never surface as an unhandled rejection in a page
  // the user is happily using.
  void writeRemoteLayout(scope, value);
};

/**
 * Queue a PUT for a key that syncs.
 *
 * Debounced because a drag emits a write per frame it settles on and a resize
 * emits one per quantised step; without this, arranging a dashboard would be a
 * dozen round trips for one intent. The LAST value wins, which is correct for
 * a full-replace endpoint.
 */
const queueRemoteWrite = (key: string, value: unknown): void => {
  if (!REMOTE_KEYS.has(key) || !KEY_SCOPES.has(key)) return;
  cancelPending(key);
  const timer = setTimeout(() => {
    PENDING.delete(key);
    sendWrite(key, value);
  }, LAYOUT_SYNC_DEBOUNCE_MS);
  PENDING.set(key, { timer, value });
};

/**
 * Send every queued write now.
 *
 * For a page that is about to go away - a `pagehide` handler, a sign-out - and
 * for tests, which must not leave a timer running past the assertion.
 */
export function flushLayoutSync(): void {
  Array.from(PENDING.entries()).forEach(([key, pending]) => {
    clearTimeout(pending.timer);
    PENDING.delete(key);
    sendWrite(key, pending.value);
  });
}

/*
  A layout edited a moment before leaving the page must not be lost to the
  debounce. `pagehide` fires on close, reload and back/forward-cache entry —
  unlike `beforeunload`, which browsers increasingly skip. This is a no-op
  server-side and in tests, where there is no window.
*/
if (typeof window !== 'undefined' && typeof window.addEventListener === 'function') {
  window.addEventListener('pagehide', () => flushLayoutSync());
}

/** Drop all sync state. Tests only - production has one store for its life. */
export function resetLayoutSync(): void {
  PENDING.forEach((pending) => clearTimeout(pending.timer));
  PENDING.clear();
  REMOTE_KEYS.clear();
}

/**
 * Bring a scope up to date with the server, and enrol it in future syncs.
 *
 * The reconciliation is the whole point, and it is three cases:
 *
 *   SERVER HAS ONE   → it wins. Written into the cache directly (not through
 *                      `writeScoped`, which would immediately PUT it straight
 *                      back) and handed to the caller so the surface can
 *                      re-render on it.
 *   SERVER HAS NONE, LOCAL DOES → this is the migration. The arrangement this
 *                      person built before the endpoint existed is pushed up
 *                      once, so it follows them from now on instead of being
 *                      silently replaced by the defaults on their next device.
 *   SERVER UNREACHABLE → nothing happens at all. The cache is already on
 *                      screen, and the key is NOT enrolled, so we do not
 *                      queue writes we cannot deliver.
 *
 * Returns the layout the caller should adopt, or `null` to keep what it has.
 */
export async function hydrateScoped(
  key: string,
  storage: LayoutStorage | null = defaultStorage()
): Promise<unknown | null> {
  const scope = KEY_SCOPES.get(key);
  if (!scope) return null;

  const result = await readRemoteLayout(scope);
  if (!result.ok) return null;

  REMOTE_KEYS.add(key);

  if (result.layout !== null && result.layout !== undefined) {
    writeLocal(key, result.layout, storage);
    return result.layout;
  }

  const local = readScoped(key, storage);
  if (local !== null) sendWrite(key, local);
  return null;
}

/** Whether a key currently syncs - for tests and for diagnostics. */
export const isSyncedScope = (key: string): boolean => REMOTE_KEYS.has(key);

/* ------------------------------------------------------------------ */
/*  Storage access                                                     */
/* ------------------------------------------------------------------ */

/**
 * The slice of `Storage` this module uses.
 *
 * Narrow on purpose: tests hand in a plain object, and nothing here needs
 * `length`/`key(i)` except the prune and the migration, which take the key
 * list separately.
 */
export interface LayoutStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
  /** Present on a real `Storage`; the prune and migration need it. */
  readonly length?: number;
  key?(index: number): string | null;
}

const defaultStorage = (): LayoutStorage | null => {
  try {
    return typeof window === 'undefined' ? null : window.localStorage;
  } catch {
    // Storage disabled (private mode, blocked cookies). Layout is a
    // convenience; the surface still works on its defaults.
    return null;
  }
};

/** Every key in the storage, best effort - a memory stub may not offer any. */
const allKeys = (storage: LayoutStorage): string[] => {
  if (typeof storage.key !== 'function' || typeof storage.length !== 'number') {
    // A plain-object stub used in tests: read its own enumerable keys.
    return Object.keys(storage as unknown as Record<string, unknown>).filter(
      (k) => typeof (storage as any)[k] === 'string'
    );
  }
  const keys: string[] = [];
  for (let i = 0; i < (storage.length as number); i += 1) {
    const k = storage.key(i);
    if (k) keys.push(k);
  }
  return keys;
};

/* ------------------------------------------------------------------ */
/*  Recency index + prune                                              */
/* ------------------------------------------------------------------ */

const indexKey = (): string => `${LAYOUT_KEY_PREFIX}:index`;

type RecencyIndex = Record<string, number>;

const readIndex = (storage: LayoutStorage): RecencyIndex => {
  try {
    const raw = storage.getItem(indexKey());
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return {};
    const out: RecencyIndex = {};
    Object.entries(parsed as Record<string, unknown>).forEach(([k, v]) => {
      if (typeof v === 'number' && Number.isFinite(v)) out[k] = v;
    });
    return out;
  } catch {
    return {};
  }
};

const writeIndex = (storage: LayoutStorage, index: RecencyIndex): void => {
  try {
    storage.setItem(indexKey(), JSON.stringify(index));
  } catch {
    /* see saveLayout: persistence is a convenience, not worth an error */
  }
};

/**
 * The group a key belongs to: everything except its entity id.
 *
 * `pons:layout:v1:<user>:<kind>:<name>[:<entity>]` - so the first five
 * segments are the group, and the sixth (if any) is the record.
 */
const groupOf = (key: string): string => key.split(':').slice(0, 5).join(':');

/**
 * Drop the oldest layouts in a group once it outgrows `MAX_PER_GROUP`.
 *
 * Keys with no recorded timestamp sort oldest - they predate the index, so
 * they are the least likely to be in use.
 */
export function pruneLayouts(
  group: string,
  storage: LayoutStorage = defaultStorage() as LayoutStorage,
  limit: number = MAX_PER_GROUP
): string[] {
  if (!storage) return [];
  const index = readIndex(storage);
  const members = allKeys(storage).filter(
    (k) => k.startsWith(`${LAYOUT_KEY_PREFIX}:`) && k !== indexKey() && groupOf(k) === group
  );
  if (members.length <= limit) return [];

  const byAge = members
    .slice()
    .sort((a, b) => (index[b] ?? 0) - (index[a] ?? 0));
  const evicted = byAge.slice(limit);

  evicted.forEach((key) => {
    try {
      storage.removeItem(key);
    } catch {
      /* nothing to do - the key stays and is evicted next time */
    }
    delete index[key];
  });
  writeIndex(storage, index);
  return evicted;
}

/* ------------------------------------------------------------------ */
/*  Scoped read / write - model-agnostic                               */
/* ------------------------------------------------------------------ */

/**
 * Read whatever JSON is stored under a scoped key.
 *
 * The user-scoping, the recency index and the prune are the same for every
 * layout model; only the REPAIR differs - floating panels have docks and
 * widths, dashboard widgets have spans and rows. So the store keeps the
 * namespace and hands the parsed value back untouched, and each model runs its
 * own `validate…` over it.
 *
 * `null` for a miss, and for anything unparseable: storage is untrusted input,
 * and "no stored layout" is exactly the right reading of a corrupt one.
 */
export function readScoped(
  key: string,
  storage: LayoutStorage | null = defaultStorage()
): unknown | null {
  if (!storage) return null;
  try {
    const raw = storage.getItem(key);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

/**
 * Write to the CACHE only.
 *
 * Split out of `writeScoped` for one caller: `hydrateScoped`, which has just
 * been handed the server's copy and must not turn round and PUT it back.
 */
function writeLocal(
  key: string,
  value: unknown,
  storage: LayoutStorage | null = defaultStorage()
): void {
  if (!storage) return;
  try {
    storage.setItem(key, JSON.stringify(value));
  } catch {
    // Quota, private mode, disabled storage. The arrangement is lost on
    // reload and nothing else breaks - not worth an error to the user.
    return;
  }
  const index = readIndex(storage);
  index[key] = Date.now();
  writeIndex(storage, index);
  pruneLayouts(groupOf(key), storage);
}

/**
 * Write a layout: cache now, server shortly.
 *
 * The cache write is synchronous and unconditional, so a reload is instant and
 * an offline session still remembers. The PUT is debounced and only happens
 * for a key that has hydrated - see the module doc.
 */
export function writeScoped(
  key: string,
  value: unknown,
  storage: LayoutStorage | null = defaultStorage()
): void {
  writeLocal(key, value, storage);
  queueRemoteWrite(key, value);
}

/**
 * Forget one scoped key - its surface falls back to the defaults.
 *
 * This is "reset to default", so it has to reach the server: leaving the row
 * there would mean the next load hydrated the very arrangement the user just
 * threw away. A queued PUT is cancelled first, or the debounce would resurrect
 * it a moment after the DELETE.
 */
export function clearScoped(
  key: string,
  storage: LayoutStorage | null = defaultStorage()
): void {
  cancelPending(key);
  const scope = KEY_SCOPES.get(key);
  if (scope && REMOTE_KEYS.has(key)) void deleteRemoteLayout(scope);

  if (!storage) return;
  try {
    storage.removeItem(key);
  } catch {
    return;
  }
  const index = readIndex(storage);
  if (index[key]) {
    delete index[key];
    writeIndex(storage, index);
  }
}

/* ------------------------------------------------------------------ */
/*  Get / set - the floating-panel model                               */
/* ------------------------------------------------------------------ */

/**
 * Read a surface's stored layout, repaired.
 *
 * `surface.storageKey` is a scoped key built by `scopeKey`. Anything the
 * stored value gets wrong - a retired pane id, a width outside the current
 * bounds, outright junk - is repaired by `validateLayout` exactly as the
 * unscoped loader did.
 */
export function getLayout<Id extends string>(
  surface: PanelSurface<Id>,
  storage: LayoutStorage | null = defaultStorage()
): PanelLayout<Id> {
  if (!storage) return surface.defaults;
  const raw = readScoped(surface.storageKey, storage);
  if (raw === null) return surface.defaults;
  return validateLayout(surface, raw);
}

/** Write a surface's layout, touch its recency, and prune its group. */
export function setLayout<Id extends string>(
  surface: PanelSurface<Id>,
  layout: PanelLayout<Id>,
  storage: LayoutStorage | null = defaultStorage()
): void {
  writeScoped(surface.storageKey, layout, storage);
}

/** Forget one surface's layout - it falls back to the defaults. */
export function clearLayout<Id extends string>(
  surface: PanelSurface<Id>,
  storage: LayoutStorage | null = defaultStorage()
): void {
  clearScoped(surface.storageKey, storage);
}

/* ------------------------------------------------------------------ */
/*  Migration off the unscoped keys                                    */
/* ------------------------------------------------------------------ */

/**
 * One legacy key, or family of keys, and where it now lives.
 *
 * `exact` is a single key (review's one session-shaped workspace). `prefix`
 * matches a key per record - everything after it is the entity id, which is
 * how `matter:layout:v1:<matterId>` becomes an entity-scoped key.
 */
export interface LegacyLayoutRule {
  exact?: string;
  prefix?: string;
  scope: Omit<LayoutScope, 'userId' | 'entityId'>;
}

const ruleId = (rule: LegacyLayoutRule): string => rule.exact ?? `${rule.prefix}*`;

const migrationMarker = (userId: string): string =>
  `${LAYOUT_KEY_PREFIX}:migrated:${segment(userId || ANONYMOUS_USER)}`;

/**
 * Copy the unscoped layouts already in this browser into the user's namespace.
 *
 * Runs at most once per rule per user (a marker records which rules are
 * done), and never overwrites a scoped layout that already exists - the
 * user's current arrangement always wins over an older unscoped one.
 *
 * The originals are LEFT IN PLACE. They are a few hundred bytes, and deleting
 * them would strand a second profile on the same machine that has not opened
 * the app since this shipped.
 *
 * Returns the scoped keys written.
 */
export function migrateLegacyLayouts(
  userId: string,
  rules: LegacyLayoutRule[],
  storage: LayoutStorage | null = defaultStorage()
): string[] {
  if (!storage || rules.length === 0) return [];

  const marker = migrationMarker(userId);
  let done: string[] = [];
  try {
    const raw = storage.getItem(marker);
    const parsed = raw ? JSON.parse(raw) : null;
    if (Array.isArray(parsed)) done = parsed.filter((v) => typeof v === 'string');
  } catch {
    done = [];
  }

  const pending = rules.filter((rule) => !done.includes(ruleId(rule)));
  if (pending.length === 0) return [];

  const keys = allKeys(storage);
  const written: string[] = [];

  pending.forEach((rule) => {
    const sources: Array<{ key: string; entityId?: string }> = [];
    if (rule.exact && keys.includes(rule.exact)) sources.push({ key: rule.exact });
    if (rule.prefix) {
      keys
        .filter((k) => k.startsWith(rule.prefix as string) && k.length > (rule.prefix as string).length)
        .forEach((k) => sources.push({ key: k, entityId: k.slice((rule.prefix as string).length) }));
    }

    sources.forEach(({ key, entityId }) => {
      const value = storage.getItem(key);
      if (!value) return;
      const target = scopeKey({ ...rule.scope, userId, entityId });
      // Never clobber: an arrangement made since this shipped is newer than
      // anything sitting under an unscoped key.
      if (storage.getItem(target) !== null) return;
      try {
        storage.setItem(target, value);
        written.push(target);
      } catch {
        /* out of quota - the legacy key stays, nothing is lost */
      }
    });
  });

  try {
    storage.setItem(marker, JSON.stringify([...done, ...pending.map(ruleId)]));
  } catch {
    /* the marker is an optimisation; re-running is idempotent anyway */
  }

  // Newly copied keys have no recorded recency, so give them one - otherwise
  // a migrated layout sorts oldest and is the first thing pruned.
  if (written.length > 0) {
    const index = readIndex(storage);
    const now = Date.now();
    written.forEach((key) => { index[key] = now; });
    writeIndex(storage, index);
    Array.from(new Set(written.map(groupOf))).forEach((group) =>
      pruneLayouts(group, storage)
    );
  }

  return written;
}

const layoutStore = {
  scopeKey,
  readScoped,
  writeScoped,
  clearScoped,
  getLayout,
  setLayout,
  clearLayout,
  pruneLayouts,
  migrateLegacyLayouts,
};

export default layoutStore;
